import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { Badge } from "@/components/badge";
import type { IUser } from "@/apis/users/getMe";
import { useQueryGetUserList, apiGenerateToken } from "@/apis/users";
import { useNavigate } from "react-router-dom";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/dropdown";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { message as AntdMessage } from "antd";
import useLoginStore from "@/stores/loginStore";
import { getUserRoleFromToken } from "@/utils/jwt";
import { Button } from "@/components/button";
import SortableTableHeader from "@/components/SortableTableHeader";
import GroupBadge from "@/components/GroupBadge";

type SortField = "grade" | "name" | "school" | null;
type SortOrder = "asc" | "desc";

interface UserListTableProps {
  users: IUser[];
  isLoading: boolean;
  error: Error | null;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}

const UserListTable: React.FC<UserListTableProps> = ({
  users,
  isLoading,
  error,
  sortField,
  sortOrder,
  onSort,
}) => {
  const navigate = useNavigate();
  const { setToken } = useLoginStore();

  // Navigate to student detail page
  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleEditUser = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/users/${userId}/edit`);
  };

  const handleGenerateToken = async (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await apiGenerateToken(userId);
      if (response.success && response.data?.token) {
        setToken(response.data.token);
        AntdMessage.success("Token generated and logged in successfully!");

        // 역할에 따라 리다이렉트
        const role = getUserRoleFromToken(response.data.token);
        if (role === "student") {
          navigate("/my_exams");
        } else if (role === "admin") {
          navigate("/exam_papers");
        } else {
          navigate("/");
        }
      }
    } catch (error: any) {
      AntdMessage.error(error.message || "Failed to generate token");
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading students...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error occurred: {error.toString()}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">No students found.</div>
    );
  }

  return (
    <Table className="mt-4">
      <TableHead>
        <TableRow>
          <SortableTableHeader
            sortKey="name"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            Name
          </SortableTableHeader>
          <TableHeader>Email</TableHeader>
          <SortableTableHeader
            sortKey="grade"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            Grade
          </SortableTableHeader>
          <SortableTableHeader
            sortKey="school"
            currentSortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
          >
            School
          </SortableTableHeader>
          <TableHeader>Group</TableHeader>
          <TableHeader>Phone</TableHeader>
          <TableHeader className="w-[200px]">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.map((user) => (
          <TableRow
            key={user.id}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => handleViewUser(user.id)}
          >
            <TableCell>
              <div className="font-medium">{user.name}</div>
            </TableCell>
            <TableCell
              onClick={(e) => e.stopPropagation()}
              className="cursor-text"
            >
              {user.email}
            </TableCell>
            <TableCell className="text-zinc-500">
              {user.is_graduated
                ? "Graduated"
                : user.grade === 0
                ? "-"
                : `${user.grade}`}
            </TableCell>
            <TableCell>{user.school || "-"}</TableCell>
            <TableCell>
              <GroupBadge group={user.group} />
            </TableCell>
            <TableCell>{user.phone || "-"}</TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Button
                  onClick={(e: React.MouseEvent) => handleEditUser(user.id, e)}
                >
                  Edit
                </Button>
                <Dropdown>
                  <DropdownButton
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <EllipsisHorizontalIcon className="h-5 w-5" />
                  </DropdownButton>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={(e: React.MouseEvent) =>
                        handleGenerateToken(user.id, e)
                      }
                    >
                      이 계정으로 로그인
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default UserListTable;
